import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TableChartIcon from '@mui/icons-material/TableChart'
import DownloadIcon from '@mui/icons-material/Download'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import CategoryIcon from '@mui/icons-material/Category'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import useRouter from '../hooks/useRouter'
import { API_BASE_URL } from '../constants/system'
import useAccount from '../hooks/useAccount'
import useAutoCoder from '../hooks/useAutoCoder'
import useFrameworkLibrary from '../hooks/useFrameworkLibrary'
import { extractErrorMessage } from '../utils/apitools'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import PageHeader from '../components/system/PageHeader'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import ActionButton from '../components/system/ActionButton'
import { Divider } from '@mui/material'
import AutoCoderStatusChip from '../components/autocoder/AutoCoderStatusChip'
import PipelineQueueStrip from '../components/autocoder/PipelineQueueStrip'
import ExtractFrameworkDialog from '../components/autocoder/ExtractFrameworkDialog'
import { AutoCoderPipeline, DataImportStatus } from '../types/gotypes'
import { showsPipelineSwitches } from '../utils/pipelineSwitches'
import { pipelineDisplayStatus } from '../utils/pipelineDisplayStatus'
import { buildRowQueue } from '../utils/pipelineRowQueue'
import { PipelineSectionId } from '../utils/pipelineSections'
import { useAutocoderThroughput } from '../hooks/useAutocoderThroughput'

import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools, voiceCollection } from '../voice/buildVoiceTools'
import { VoiceUITool } from '../voice/types'
import { useDisplayNames } from '../utils/displayNames'

interface AutoCoderPageProps {
  title?: string
  description?: string
  newButtonText?: string
  defaultLabels?: Record<string, string>
  newPipelineLabels?: string
  // Mounted inside the AutoCoder shell's Pipelines tab: the shell owns the
  // viewport height, and "Code Frames" switches tab instead of navigating away.
  embedded?: boolean
  onViewFrameworks?: () => void
}

const AutoCoderPage: React.FC<AutoCoderPageProps> = ({
  title = 'AutoCoder Pipelines',
  description,
  newButtonText = 'New Pipeline',
  defaultLabels,
  newPipelineLabels,
  embedded = false,
  onViewFrameworks,
}) => {
  const router = useRouter()
  const account = useAccount()
  const [deleteTarget, setDeleteTarget] = useState<AutoCoderPipeline | null>(null)
  // The pipelines helper (Douglas plan T16).
  const [helperOpen, setHelperOpen] = useState(false)
  const [startCodingTarget, setStartCodingTarget] = useState<AutoCoderPipeline | null>(null)
  const [extractTarget, setExtractTarget] = useState<AutoCoderPipeline | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('long-lived')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  // Fetch pipelines (filtered by defaultLabels if provided)
  const {
    pipelines,
    pipelinesTotal,
    pipelinesLoading,
    pipelinesError,
    startPipeline,
    deletePipeline,
    deleteLoading,
    refetch,
  } = useAutoCoder({ customer, job, labels: defaultLabels, page, rowsPerPage, lifecycle: lifecycleFilter })

  // The row queue strip's second source. ONE customer-scoped request feeds
  // every row (see utils/pipelineRowQueue.ts for why this, and not an N+1 of
  // per-pipeline calls). The endpoint is realm-admin gated, so it is only
  // asked for when the viewer can actually have it — and the strip degrades to
  // the record total for everyone else rather than showing an error.
  const canSeeDeficit = Boolean(
    account.user?.role_permissions?.isManager || account.user?.role_permissions?.isRealmAdmin,
  )
  const throughput = useAutocoderThroughput(customer, '60m', canSeeDeficit)
  const deficitRows = canSeeDeficit ? throughput.data?.deficit.pipelines : undefined

  // Framework library for extracting frameworks
  const { extractFramework, extractLoading } = useFrameworkLibrary({ customer, job })

  // Filter options derived from data — the same status the row's chip shows
  // (lifecycle for long-lived, transient state for one-shot), so what you
  // filter by is what you see.
  const statusOptions = useMemo(() => {
    const statuses = new Set(pipelines.map(pipelineDisplayStatus))
    return Array.from(statuses).sort()
  }, [pipelines])

  // pipelines is already the server-side page for (page, rowsPerPage, lifecycle).
  // statusFilter refines within the current page only.
  const visiblePipelines = useMemo(() => {
    if (statusFilter === 'all') return pipelines
    return pipelines.filter(p => pipelineDisplayStatus(p) === statusFilter)
  }, [pipelines, statusFilter])

  const handleLifecycleChange = (value: string) => {
    setLifecycleFilter(value)
    setPage(0)
  }

  const handleAddClick = () => {
    if (!customer || !job) return
    router.navigate('autocoder-pipeline-edit', {
      customer,
      job,
      pipelineId: 'new',
      ...(newPipelineLabels && { labels: newPipelineLabels }),
    })
  }

  const handleViewFrameworks = () => {
    if (onViewFrameworks) {
      onViewFrameworks()
      return
    }
    if (!customer || !job) return
    router.navigate('frameworks', { customer, job })
  }

  const handleBatchImport = () => {
    if (!customer || !job) return
    router.navigate('batch-import', { customer, job })
  }

  // Deletion is destructive and irreversible, so it is hand-rolled (rather than
  // using the generic `costly` gate, whose message is written for token-spend
  // actions) with its own confirm-first contract: without confirmed=true it
  // only reports what would be deleted; the delete only runs once the model
  // calls again with confirmed=true after the user has agreed.
  const deletePipelineVoiceTool: VoiceUITool = {
    name: 'delete_pipeline',
    description:
      'Delete a pipeline, identified by the id from find_pipeline. Destructive and cannot be undone. ' +
      'Always confirm with the user first: call this without confirmed=true to get a confirmation message, ' +
      'read it back to the user, and only call again with confirmed=true once they explicitly agree.',
    meta: { category: 'item-action', collection: 'pipeline', confirm: 'click' },
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The pipeline id (from find_pipeline).' },
        confirmed: {
          type: 'boolean',
          description: 'Set true only after the user has explicitly confirmed the deletion.',
        },
      },
      required: ['id'],
      additionalProperties: false,
    },
    execute: (args) => {
      const id = String(args.id ?? '')
      const pipeline = pipelines.find((p) => p.id === id)
      if (!pipeline) {
        return JSON.stringify({ error: `no pipeline with id "${id}" — call find_pipeline first` })
      }
      const name = pipeline.config?.name || pipeline.id
      if (args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `About to delete "${name}" — this cannot be undone. Say yes to confirm, then call again with confirmed=true.`,
        })
      }
      deletePipeline(id)
      return JSON.stringify({ ok: true, did: 'delete', id })
    },
  }

  // Expose this page's interaction points to the voice assistant. These drive
  // the same handlers as the on-screen buttons, so voice does exactly what
  // clicking the equivalent control does.
  // Douglas reads this back aloud, so it carries display names rather than
  // the codes in the URL (#1347 item 1).
  const names = useDisplayNames(customer)

  useVoiceRegistration({
    pageKey: `autocoder:${customer}:${job}`,
    pageContext: () =>
      `AutoCoder Pipelines page for customer ${names.customer}, job ${names.job(job)}. ${pipelines.length} pipeline(s) listed` +
      (statusFilter !== 'all' ? `, filtered by status "${statusFilter}"` : '') +
      `. Available actions: create a new pipeline (opens the pipeline wizard), view coding frameworks, start a batch import.`,
    uiTools: [
      ...buildVoiceTools({
        choices: [
          {
            name: 'filter_pipelines_by_status',
            label: 'the pipeline status filter',
            options: ['all', ...statusOptions],
            select: (v) => {
              setStatusFilter(v)
              setPage(0)
            },
          },
        ],
        actions: [
          {
            name: 'create_new_pipeline',
            label: 'create a new pipeline',
            run: () => handleAddClick(),
          },
          {
            name: 'view_coding_frameworks',
            label: 'open the coding frameworks',
            run: () => handleViewFrameworks(),
          },
          {
            name: 'start_batch_import',
            label: 'start a batch import',
            run: () => handleBatchImport(),
          },
        ],
        collections: [
          voiceCollection<AutoCoderPipeline>({
            name: 'pipeline',
            label: 'pipeline',
            list: () => pipelines,
            identify: (p) => ({ id: p.id, label: p.config?.name || p.id, keywords: pipelineDisplayStatus(p) }),
            actions: [
              {
                name: 'start_coding_pipeline',
                label: 'start coding on',
                costly: true,
                note: 'Runs the AutoCoder over this pipeline\'s data.',
                run: async (p) => {
                  await startPipeline(p.id)
                  openPipelinePage(p.id, 'status')
                },
              },
              { name: 'edit_pipeline', label: 'edit', run: (p) => handleEditClick(p) },
              { name: 'view_pipeline_status', label: 'open the status of', run: (p) => handleViewStatusClick(p) },
              { name: 'download_pipeline_csv', label: 'download the coded CSV for', run: (p) => handleDownloadCSV(p) },
            ],
          }),
        ],
      }),
      deletePipelineVoiceTool,
    ],
  })

  const handleEditClick = (pipeline: AutoCoderPipeline) => {
    if (!customer || !job) return
    // Straight to the pipeline's Settings section. The edit route builds a NEW
    // pipeline and now redirects here anyway; going direct avoids the bounce.
    router.navigate('autocoder-pipeline', {
      customer,
      job,
      pipelineId: pipeline.id,
      section: 'settings',
    })
  }

  const handleDeleteClick = (pipeline: AutoCoderPipeline) => {
    setDeleteTarget(pipeline)
  }

  const handleStartCodingClick = (pipeline: AutoCoderPipeline) => {
    setStartCodingTarget(pipeline)
  }

  // The eye icon (and every deep-link into a pipeline) now goes to the
  // pipeline's own PAGE, on the requested section. The status modal it used to
  // open has been deleted — nothing else referenced it.
  const openPipelinePage = (id: string, section?: PipelineSectionId) => {
    if (!customer || !job) return
    router.navigate('autocoder-pipeline', {
      customer,
      job,
      pipelineId: id,
      ...(section ? { section } : {}),
    })
  }

  const handleViewStatusClick = (pipeline: AutoCoderPipeline) => {
    openPipelinePage(pipeline.id)
  }

  const handleDownloadCSV = (pipeline: AutoCoderPipeline) => {
    const url = `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipeline.id}/csv?access_token=${account.user?.token}`
    window.open(url, '_blank')
  }

  const handleDownloadRawCSV = (pipeline: AutoCoderPipeline) => {
    const url = `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipeline.id}/raw-csv?access_token=${account.user?.token}`
    window.open(url, '_blank')
  }

  const handleOpenWithTab = (pipelineId: string, section: PipelineSectionId) => {
    openPipelinePage(pipelineId, section)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget || deleteLoading) return
    deletePipeline(deleteTarget.id)
    setDeleteTarget(null)
  }

  const handleCancelDelete = () => {
    if (deleteLoading) return
    setDeleteTarget(null)
  }

  const handleConfirmStartCoding = async () => {
    if (!startCodingTarget) return
    const pipelineId = startCodingTarget.id
    setStartCodingTarget(null)
    await startPipeline(pipelineId)
    openPipelinePage(pipelineId, 'status')
  }

  const handleCancelStartCoding = () => {
    setStartCodingTarget(null)
  }

  const handleExtractFramework = (pipeline: AutoCoderPipeline) => {
    setExtractTarget(pipeline)
  }

  const handleConfirmExtract = async (name: string, description: string) => {
    if (!extractTarget) return
    await extractFramework(extractTarget.id, { name, description })
    setExtractTarget(null)
  }

  const handleCancelExtract = () => {
    setExtractTarget(null)
  }

  // Helper to get framework engine display string
  const getFrameworkDisplay = (pipeline: AutoCoderPipeline): string => {
    const config = pipeline.config
    if (!config?.import_type) return '—'

    const frameworks = config?.frameworks || []
    const frameworkCount = frameworks.length

    // Calculate total codes across all frameworks
    const totalCodes = frameworks.reduce((sum, fw) => {
      const mainCodes = fw.main_variable?.codes?.length || 0
      const loopedCodes = fw.looped_variables?.reduce(
        (acc, lv) => acc + (lv.codes?.length || 0), 0
      ) || 0
      return sum + mainCodes + loopedCodes
    }, 0)

    if (frameworkCount === 0) return '—'
    return `${frameworkCount} framework${frameworkCount !== 1 ? 's' : ''}, ${totalCodes} codes`
  }

  // Helper to format date
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return '—'
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const renderTableBody = () => {
    if (pipelinesLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align='center'>
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      )
    }

    if (pipelinesError) {
      const message = pipelinesError instanceof Error ? pipelinesError.message : 'Unable to load pipelines.'
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Alert severity='error'>{message}</Alert>
          </TableCell>
        </TableRow>
      )
    }

    if (!pipelines.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} align='center'>
            <Typography variant='body2' color='text.secondary'>
              No pipelines yet. Click "New Pipeline" to create your first AutoCoder pipeline.
            </Typography>
          </TableCell>
        </TableRow>
      )
    }

    if (!visiblePipelines.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} align='center'>
            <Typography variant='body2' color='text.secondary'>
              No pipelines match the current filters.
            </Typography>
          </TableCell>
        </TableRow>
      )
    }

    return visiblePipelines.map(pipeline => {
      const config = pipeline.config
      const nameDisplay = config?.name || '(Unnamed)'
      const pipelineState = pipeline.state || 'unknown'
      // Only draft ('created') pipelines that haven't failed can be edited via the wizard.
      // 'preparing' and beyond show the view modal instead (pipeline is already dispatched).
      const canEdit = pipelineState === 'created'
        && pipeline.data_status !== 'failed' && pipeline.coding_status !== 'failed'
      const canDelete = true // Allow deletion in any state

      // Check if pipeline has a coding framework defined
      const hasFramework = config?.import_type === 'manual'
        ? (config?.frameworks?.[0]?.main_variable?.codes?.length ?? 0) > 0
        : (config?.framework_engine?.themeai?.themes?.length ?? 0) > 0
      // Only show start button for 'created' state (not 'preparing') and when data import hasn't failed
      const hasValidState = pipelineState === 'created' && pipeline.data_status !== 'failed'
      const canStart = hasValidState && hasFramework

      // Whether to offer the coded CSV. A one-shot pipeline has a finish line,
      // so it keeps the legacy predicate. A long-lived one never reaches
      // 'completed' — it codes continuously — so gating its download on those
      // words hid the file forever AND left the button's presence unexplainable
      // from the row, whose chip speaks the coding gate instead.
      const hasCodedCSV = showsPipelineSwitches(pipeline)
        || pipelineState === 'completed'
        || pipelineState === 'imported'

      // Get record count from config.total_cases
      const recordCount = config?.total_cases || 0

      return (
        <TableRow key={pipeline.id} hover>
          <TableCell>
            <Typography variant="body2" fontWeight={500}>
              {nameDisplay}
            </Typography>
            {/* Stable identity the connectors and consolidation key on. Only
                long-lived pipelines have one — render nothing when absent. */}
            {pipeline.system_name && (
              <Tooltip title="System name — the stable ID connectors address this pipeline by">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontFamily: 'monospace', width: 'fit-content' }}
                >
                  {pipeline.system_name}
                </Typography>
              </Tooltip>
            )}
            {config?.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {config.description.length > 50 ? config.description.slice(0, 50) + '...' : config.description}
              </Typography>
            )}
          </TableCell>

          <TableCell>
            {/* The display status folds in the coding gate and failure
                precedence — see utils/pipelineDisplayStatus.ts. The chip is
                read-only on purpose: the switch that CHANGES coding lives in
                the pipeline's Settings, so a glance at the list cannot flip
                spend by accident. */}
            <AutoCoderStatusChip
              state={pipelineDisplayStatus(pipeline)}
              error={pipeline.data_error || pipeline.coding_error}
            />
            {/* Every pipeline is a coding pipeline since F1 retired the
                scrape_only mode, so every row gets the queue strip. */}
            <Box sx={{ mt: 0.5 }}>
              <PipelineQueueStrip model={buildRowQueue(pipeline, deficitRows)} />
            </Box>
          </TableCell>

          <TableCell>
            <Box
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => handleOpenWithTab(pipeline.id, 'frameworks')}
            >
              <Typography variant="body2">
                {getFrameworkDisplay(pipeline)}
              </Typography>
            </Box>
          </TableCell>

          <TableCell>
            <Box
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => handleOpenWithTab(pipeline.id, 'results')}
            >
              <Typography variant="body2">
                {recordCount > 0 ? `${recordCount} records` : '—'}
              </Typography>
            </Box>
          </TableCell>

          <TableCell>
            <Typography variant="body2">
              {formatDate(pipeline.created_at)}
            </Typography>
          </TableCell>

          <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
            {/* Start button - requires valid state and framework defined */}
            {hasValidState && (
              <Tooltip title={hasFramework ? 'Start Processing' : 'Define a coding framework first'}>
                {/* data-voice="confirm": Douglas asks before pressing (#1363) — starts spending AI tokens on coding */}
                <span data-voice="confirm">
                  <IconButton
                    aria-label='Start Processing'
                    onClick={() => handleStartCodingClick(pipeline)}
                    color="info"
                    disabled={!hasFramework}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Edit button - only for preparing/created states (opens wizard) */}
            {canEdit && (
              <Tooltip title="Edit Pipeline">
                <IconButton
                  aria-label='Edit Pipeline'
                  onClick={() => handleEditClick(pipeline)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* View button - for non-editable states (opens status modal) */}
            {!canEdit && (
              <Tooltip title="View Pipeline">
                <IconButton
                  aria-label='View Pipeline'
                  onClick={() => handleViewStatusClick(pipeline)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Download coded CSV — see hasCodedCSV above */}
            {hasCodedCSV && (
              <Tooltip title="Download coded CSV">
                <IconButton
                  aria-label='Download coded CSV'
                  onClick={() => handleDownloadCSV(pipeline)}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Download raw CSV - available when data is ready */}
            {pipeline.data_status === DataImportStatus.ready && (
              <Tooltip title="Download raw CSV">
                <IconButton
                  aria-label='Download raw CSV'
                  color="info"
                  onClick={() => handleDownloadRawCSV(pipeline)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Save as Framework - only if pipeline has a framework defined */}
            {hasFramework && (
              <Tooltip title="Save as Framework">
                <IconButton
                  aria-label='Save as Framework'
                  onClick={() => handleExtractFramework(pipeline)}
                >
                  <SaveAltIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Delete button - only for preparing/created states */}
            {canDelete && (
              <Tooltip title="Delete Pipeline">
                <IconButton
                  aria-label='Delete Pipeline'
                  onClick={() => handleDeleteClick(pipeline)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: embedded ? '100%' : 'calc(100vh - 64px)' }}>
      <PageHeader
        title={title}
        subtitle={description || `${account.customer?.displayName || customer} / ${account.job?.displayName || job}`}
        actions={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {!embedded && (
              <PageAgentButton
                pageKey="autocoder"
                customer={account.customer?.name || customer}
                open={helperOpen}
                onClick={() => setHelperOpen(o => !o)}
              />
            )}
            <ActionButton icon={<CategoryIcon />} label="Code Frames" onClick={handleViewFrameworks} />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
            <ActionButton icon={<UploadFileIcon />} label="Batch Import" onClick={handleBatchImport} />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
            <ActionButton icon={<AddIcon />} label={newButtonText} onClick={handleAddClick} />
          </Box>
        }
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="lifecycle-filter-label">Type</InputLabel>
            <Select
              labelId="lifecycle-filter-label"
              label="Type"
              value={lifecycleFilter}
              onChange={(e) => handleLifecycleChange(e.target.value)}
            >
              <MenuItem value="long-lived">Long-lived</MenuItem>
              <MenuItem value="one-shot">One-shot</MenuItem>
              {/* "Long-lived" is active|paused only, so archived pipelines need
                  their own option to stay findable. */}
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="any">Any</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
              displayEmpty
              renderValue={(v) => `Status: ${v === 'all' ? 'All' : v}`}
              inputProps={{ 'aria-label': 'Filter by status' }}
            >
              <MenuItem value="all">All</MenuItem>
              {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {visiblePipelines.length} of {pipelines.length} pipelines
          </Typography>
        </Stack>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 250 }}>Name</TableCell>
                <TableCell sx={{ width: 140 }}>Status</TableCell>
                <TableCell sx={{ width: 140 }}>Framework</TableCell>
                <TableCell sx={{ width: 100 }}>Records</TableCell>
                <TableCell sx={{ width: 100 }}>Created</TableCell>
                <TableCell sx={{ width: 150 }} align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pipelinesTotal}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Box>

      <DeleteConfirmWindow
        open={Boolean(deleteTarget)}
        title='Delete Pipeline'
        message={`Are you sure you want to delete "${deleteTarget?.config?.name || deleteTarget?.id}"? This action cannot be undone.`}
        confirmWord='delete'
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <DeleteConfirmWindow
        open={Boolean(startCodingTarget)}
        title='Start Processing'
        message={`Are you sure you want to start the coding process for "${startCodingTarget?.config?.name || startCodingTarget?.id}"?`}
        confirmWord='confirm'
        onConfirm={handleConfirmStartCoding}
        onCancel={handleCancelStartCoding}
      />

      <ExtractFrameworkDialog
        open={Boolean(extractTarget)}
        pipeline={extractTarget}
        onClose={handleCancelExtract}
        onExtract={handleConfirmExtract}
        isLoading={extractLoading}
      />

      {!embedded && (
        <PageAgentDrawer
          pageKey="autocoder"
          customer={account.customer?.name || customer}
          job={job}
          open={helperOpen}
          onClose={() => setHelperOpen(false)}
        />
      )}
    </Box>
  )
}

export default AutoCoderPage
